import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface DashboardCardProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  isPlaceholder?: boolean;
  placeholderText?: string;
  action?: React.ReactNode;
  variant?: 'default' | 'highlight' | 'success' | 'warning';
}

const DashboardCard = ({ 
  title, 
  description, 
  icon, 
  children, 
  className,
  isPlaceholder = false,
  placeholderText = "Feature coming soon",
  action,
  variant = 'default'
}: DashboardCardProps) => {
  const variantStyles = {
    default: "border-border/50",
    highlight: "border-peach-orange/30 bg-gradient-to-br from-warm-cream to-white",
    success: "border-emerald-200/60 bg-gradient-to-br from-emerald-50/30 to-white",
    warning: "border-sunset-yellow/40 bg-gradient-to-br from-sunset-yellow/10 to-white"
  };

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-peach-orange/5",
      variantStyles[variant],
      isPlaceholder && "opacity-75",
      className
    )}>
      {isPlaceholder && (
        <Badge 
          variant="secondary" 
          className="absolute top-3 right-3 bg-sunset-yellow/20 text-sunset-dark border-sunset-yellow/30 z-10"
        >
          {placeholderText}
        </Badge>
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {icon && <div className="text-peach-orange">{icon}</div>}
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
          </div>
          {action && <div>{action}</div>}
        </div>
        {description && (
          <CardDescription className="text-sm">{description}</CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="pt-0">
        {children}
      </CardContent>
    </Card>
  );
};

export default DashboardCard;